/**
 * RecentActivityFeed — the dashboard's chronology, on the shared surface.
 *
 * It used to hand-roll the whole thing: a 20-entry `ACTION_CONFIG` map of
 * per-action colour pairs (teal, sky, slate — none of them brand tokens), its
 * own vertical rail, its own icon discs, its own relative-time rendering. That
 * is precisely the drift `shared/activity` exists to stop. Chronologies in this
 * app are ONE family: rows normalised through `activityModel`, coloured from
 * `ActivityEntry.kind` via `activityPresentation`, timestamped through
 * `feedTime`, drawn by `ActivityPane`.
 *
 * WHY THE AVATARS ARE ON HERE and off on most audit surfaces: the summary
 * endpoint filters this list to `AuditLog.user_id == <me>`, so every row is
 * the signed-in user's own action. The actor is not a name we have to resolve
 * — it is the person reading the page — so the disc can be their real picture
 * rather than a column of identical initials.
 */

import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PiClockCounterClockwiseLight } from 'react-icons/pi';
import { useAppSelector, RootState } from '../../../store';
import { DashboardActivity } from '../../../store/reducer/DashboardReducer';
import GlassCard from '../../shared/brand/GlassCard';
import Button from '../../shared/TailwindComponents/Button';
import EmptyState from '../../shared/TailwindComponents/EmptyState';
import ActivityPane from '../../shared/activity/ActivityPane';
import {
  ActivityEntry,
  activityActionLabel,
  activityKind,
  decodeEncodedTokens,
} from '../../shared/activity/activityModel';

interface RecentActivityFeedProps {
  activities: DashboardActivity[];
  loading?: boolean;
  onRefresh?: () => void;
}

/** How many rows the card carries before the full log takes over. */
const MAX_ROWS = 12;

const prettyEntity = (entityType: string): string =>
  (entityType || '').replace(/_/g, ' ').trim();

/**
 * A summary row as a timeline entry.
 *
 * `details` is the backend's own sentence when it has one; otherwise the
 * entity type is the subject, which is what the old `formatActivityMessage`
 * fell back to. The ACTION is not composed into that string any more — it is
 * the entry's `action`, which the pane renders as its badge.
 */
const toEntries = (
  activities: DashboardActivity[],
  actor: string,
  actorEmail?: string,
  actorAvatar?: string | null
): ActivityEntry[] =>
  activities.slice(0, MAX_ROWS).map((activity, index) => ({
    id: String(activity.id ?? `activity-${index}`),
    actor,
    actorEmail,
    actorAvatar,
    action: activityActionLabel(activity.action || 'updated'),
    target: decodeEncodedTokens(activity.details || prettyEntity(activity.entity_type)),
    timestamp: activity.created_at || undefined,
    kind: activityKind(activity.action || ''),
    raw: activity,
  }));

const RecentActivityFeed: React.FC<RecentActivityFeedProps> = ({
  activities,
  loading = false,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const userInfo = useAppSelector((state: RootState) => state.user.userInfo);
  const actor = useMemo(() => {
    const name = [userInfo?.first_name, userInfo?.last_name].filter(Boolean).join(' ').trim();
    return name || userInfo?.name || t('You');
  }, [userInfo, t]);

  const entries = useMemo(
    /*
      Every row here is the signed-in user's own doing — the card is "what you
      changed lately" — so the avatar is theirs, and we have it. Both spellings
      are read because the profile endpoint answers `profile_picture` and the
      session payload `ProfilePicture`; taking one of them was why this drew
      initials for a user who has a picture.
    */
    () => toEntries(
      activities || [],
      actor,
      userInfo?.email,
      userInfo?.profile_picture || (userInfo as any)?.ProfilePicture || null,
    ),
    [activities, actor, userInfo?.email]
  );

  return (
    <GlassCard
      icon={<PiClockCounterClockwiseLight />}
      title={t('Recent Activity')}
      subtitle={t('What you changed lately')}
      actions={
        <Button
          variant="outline"
          size="small"
          onClick={() => history.push('/account?tab=activity')}
        >
          {t('View All')}
        </Button>
      }
      fill
      bodyClassName="flex min-h-0 flex-1 flex-col"
    >
      <ActivityPane
        entries={entries}
        loading={loading}
        density="pane"
        showActorAvatar
        // The card header already carries the way out; a second toolbar row
        // inside it is chrome about chrome.
        hideToolbar
        onRefresh={() => onRefresh?.()}
        onEntryClick={(entry) => {
          const link = (entry.raw as DashboardActivity | undefined)?.link;
          if (link) history.push(link);
        }}
        emptyState={
          <EmptyState
            density="compact"
            icon={<PiClockCounterClockwiseLight />}
            headline={t('No activity yet')}
            subtext={t('Anything you create, edit or complete shows up here.')}
          />
        }
      />
    </GlassCard>
  );
};

export default RecentActivityFeed;
